"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Select } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon } from "@/components/ui/icon";
import type { InputVariantProps } from "@/components/ui/input";
import { useMembers } from "@/features/settings/hooks/use-members";
import { useListCollaborators } from "@/lib/api/generated";
import { initials } from "@/lib/string/initials";
import { memberDisplayName } from "@/lib/string/user-display";
import type { UserResponse } from "@/lib/api/model/userResponse";

const MEMBER_LIST_PARAMS = { per_page: 100, status: "active" } as const;
const EMPTY_MEMBERS: UserResponse[] = [];

export interface UserSelectProps {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  excludeIds?: string[];
  filter?: (member: UserResponse) => boolean;
  emptyDescription?: ReactNode;
  variant?: InputVariantProps["variant"];
  size?: InputVariantProps["size"];
  /**
   * When set, the list is scoped to the project's collaborators (members with
   * access to that project) instead of all org members. Use this anywhere the
   * selected user must have project access — e.g. a sequence sender.
   */
  projectId?: string | null;
}

export function UserSelect({
  value,
  onChange,
  placeholder = "Select user...",
  disabled,
  className,
  excludeIds,
  filter,
  emptyDescription,
  variant,
  size,
  projectId,
}: UserSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  const scopeToProject = !!projectId;
  const { data, isFetching: isFetchingMembers } = useMembers(
    MEMBER_LIST_PARAMS,
    { enabled: !scopeToProject },
  );
  const collaboratorsQuery = useListCollaborators(projectId ?? "", {
    query: { enabled: scopeToProject },
  });

  // Project-scoped: map collaborators onto the member shape the list renders,
  // keying on user_id so onChange returns the user id (not the collaborator
  // row id). Org-scoped: use the org members list as before.
  const projectMembers = useMemo<UserResponse[]>(() => {
    const rows = collaboratorsQuery.data ?? [];
    return rows.map((c) => ({
      id: c.user_id,
      org_id: null,
      email: c.email,
      first_name: c.first_name ?? "",
      last_name: c.last_name ?? "",
      avatar_url: c.avatar_url ?? null,
      timezone: "",
      role: c.role ?? "",
      is_active: true,
      email_verified: true,
      created_at: c.added_at,
      updated_at: c.added_at,
    }));
  }, [collaboratorsQuery.data]);

  const members = scopeToProject
    ? projectMembers
    : (data?.members ?? EMPTY_MEMBERS);
  const isFetching = scopeToProject
    ? collaboratorsQuery.isFetching
    : isFetchingMembers;

  const excludeSet = useMemo(
    () => new Set(excludeIds ?? []),
    [excludeIds],
  );

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return members.filter((m) => {
      if (excludeSet.has(m.id)) return false;
      if (filter && !filter(m)) return false;
      if (!q) return true;
      const name = memberDisplayName(m).toLowerCase();
      return name.includes(q) || m.email.toLowerCase().includes(q);
    });
  }, [members, excludeSet, filter, debouncedQuery]);

  const selected = useMemo(
    () => members.find((m) => m.id === value) ?? null,
    [members, value],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reset keyboard highlight whenever the filtered list shape changes
    setHighlightedIndex(-1);
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  const handleSelect = useCallback(
    (member: UserResponse) => {
      onChange(member.id);
      setOpen(false);
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const selectedName = selected ? memberDisplayName(selected) : null;
  const showPending = isFetching && members.length === 0;
  const showEmpty = !showPending && filtered.length === 0;

  return (
    <Select.Root
      open={open}
      onOpenChange={handleOpenChange}
      highlightedIndex={highlightedIndex}
      onHighlightedIndexChange={setHighlightedIndex}
    >
      <Select.Trigger
        disabled={disabled}
        className={className}
        variant={variant}
        size={size}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {selected ? (
            <Avatar className="size-[18px] shrink-0">
              <AvatarImage src={selected.avatar_url ?? undefined} alt="" />
              <AvatarFallback className="text-[8px] font-medium leading-none">
                {initials(selectedName ?? "")}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="opacity-40">
              <Icon name="IconPeople" size={14} className="text-heading" />
            </div>
          )}
          <span
            className={`flex-1 text-left truncate text-sm ${selected ? "text-heading" : "text-contrast/50"}`}
          >
            {selectedName ?? placeholder}
          </span>
          <div className="opacity-40">
            <Icon
              name="IconChevronGrabberVertical"
              size={14}
              className="text-heading"
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content>
        <Select.Search
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search..."
          loading={isFetching}
        />

        <Select.List>
          {showPending ? (
            <Select.ListPending />
          ) : showEmpty ? (
            <div className="flex flex-col items-center justify-center gap-1 py-8 px-3">
              <div className="opacity-30 mb-1">
                <Icon name="IconPeople" size={32} className="text-heading" />
              </div>
              {emptyDescription ? (
                <Select.EmptyDescription>{emptyDescription}</Select.EmptyDescription>
              ) : null}
            </div>
          ) : (
            filtered.map((m, i) => {
              const name = memberDisplayName(m);
              const isSelected = m.id === value;
              const sublabel =
                m.role === "admin" ? "Admin" : undefined;
              return (
                <Select.Item
                  key={m.id}
                  index={i}
                  selected={isSelected}
                  onSelect={() => handleSelect(m)}
                >
                  <Select.ItemIcon>
                    <Avatar className="size-5 shrink-0">
                      <AvatarImage src={m.avatar_url ?? undefined} alt="" />
                      <AvatarFallback className="text-[8px] font-medium leading-none">
                        {initials(name)}
                      </AvatarFallback>
                    </Avatar>
                  </Select.ItemIcon>
                  <Select.ItemLabel selected={isSelected}>
                    {name || "Unnamed"}
                  </Select.ItemLabel>
                  {sublabel && (
                    <span className="text-contrast/40 shrink-0 text-xs">
                      {sublabel}
                    </span>
                  )}
                  {isSelected && <Select.ItemCheck />}
                </Select.Item>
              );
            })
          )}
        </Select.List>
      </Select.Content>
    </Select.Root>
  );
}
